import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { supabase } from './lib/supabase'
import { ProtectedRoute } from './components/ProtectedRoute'
import { AppShell } from './components/layout/AppShell'
import { LoginPage } from './pages/LoginPage'
import { SignupPage } from './pages/SignupPage'
import { ForgotPasswordPage } from './pages/ForgotPasswordPage'
import { ResetPasswordPage } from './pages/ResetPasswordPage'
import { DashboardPage } from './pages/DashboardPage'
import { ShiftsPage } from './pages/ShiftsPage'
import { CalendarPage } from './pages/CalendarPage'
import { EvidencePage } from './pages/EvidencePage'
import { PayPage } from './pages/PayPage'
import { RecipientsPage } from './pages/RecipientsPage'
import { RecipientDetailPage } from './pages/RecipientDetailPage'
import { BackfillPage } from './pages/BackfillPage'
import { WaitingPeriodPage } from './pages/WaitingPeriodPage'
import { ExportPage } from './pages/ExportPage'
import { SharingPage } from './pages/SharingPage'
import { SharingGuidePage } from './pages/SharingGuidePage'
import { SharedViewPage } from './pages/SharedViewPage'
import { SettingsPage } from './pages/SettingsPage'

// When a user clicks the password-reset link in their email, Supabase fires a
// PASSWORD_RECOVERY event and establishes a recovery session. Without this,
// they'd land silently on the dashboard with no way to set a new password.
// This listener catches that event anywhere in the app and routes them to the
// dedicated reset page.
function RecoveryRedirect() {
  const navigate = useNavigate()
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        navigate('/reset-password', { replace: true })
      }
    })
    return () => sub?.subscription?.unsubscribe?.()
  }, [navigate])
  return null
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <RecoveryRedirect />
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/s/:token" element={<SharedViewPage />} />

          {/* Protected routes with shared shell */}
          <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/shifts" element={<ShiftsPage />} />
            <Route path="/backfill" element={<BackfillPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/evidence" element={<EvidencePage />} />
            <Route path="/pay" element={<PayPage />} />
            <Route path="/recipients" element={<RecipientsPage />} />
            <Route path="/recipients/:id" element={<RecipientDetailPage />} />
            <Route path="/waiting-period" element={<WaitingPeriodPage />} />
            <Route path="/export" element={<ExportPage />} />
            <Route path="/sharing" element={<SharingPage />} />
            <Route path="/sharing/guide" element={<SharingGuidePage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>

          {/* Redirects */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}
